import { useCallback, useEffect, useState } from "react";
import {
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
  startAuthentication,
  startRegistration,
} from "@simplewebauthn/browser";
import type { AttendeeSession, PasskeyList, PasskeyOptions, PasskeySummary } from "@verity-connect/contracts";

/**
 * Signing in with the device instead of an emailed code.
 *
 * The browser does the interesting part: it holds the key, asks for a face or a thumb,
 * and hands back an answer only it could produce. Everything here is plumbing around
 * that, plus being careful to say nothing at all on a browser that cannot do it.
 */
export function passkeysAvailable(): boolean {
  return typeof window !== "undefined" && browserSupportsWebAuthn();
}

/**
 * True when a device refused to enrol because it already holds a passkey for this
 * account.
 *
 * The server sends the list of credentials to exclude for exactly this reason — so a
 * device already enrolled is not offered again as if it were new — and a phone obeying
 * that is not a failure. Somebody who taps "set up device sign-in" wants this device to
 * remember them, and it already does. Reporting the refusal left them reading
 * "The authenticator was previously registered" under a heading offering to do the thing
 * that had already been done.
 */
export function deviceAlreadyEnrolled(reason: unknown): boolean {
  return typeof reason === "object" && reason !== null
    && (reason as { code?: unknown }).code === "ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED";
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(await messageFrom(response));
  return (await response.json()) as T;
}

async function messageFrom(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { message?: unknown };
    if (typeof body.message === "string" && body.message) return body.message;
  } catch {
    // Falls through to the plain sentence below.
  }
  return "That did not work. Try again in a moment.";
}

export type PasskeyDoor = "attendee" | "organizer" | "presenter" | "school" | "exhibitor";

/**
 * Signs somebody in with a device they already enrolled.
 *
 * The same passkey works at every door the person has access to, because it belongs to
 * them rather than to a door. Which door this is only decides which session opens.
 */
export async function signInWithPasskey(clientId: string, door: PasskeyDoor = "attendee"): Promise<AttendeeSession> {
  const { options } = await postJson<PasskeyOptions>(`/api/${door}/auth/passkey/options`, {});
  const credential = await startAuthentication({ optionsJSON: options as never });
  return postJson<AttendeeSession>(`/api/${door}/auth/passkey`, { credential, clientId });
}

/** Adds a passkey immediately after a successful sign-in, while the session is fresh. */
export async function enrolPasskey(door: PasskeyDoor, label: string | null = null): Promise<PasskeySummary> {
  const { options } = await postJson<PasskeyOptions>(`/api/${door}/passkeys/options`, {});
  const credential = await startRegistration({ optionsJSON: options as never });
  return postJson<PasskeySummary>(`/api/${door}/passkeys/enrol`, { credential, label });
}

/**
 * Puts the passkey inside the email field's own autofill list, where the browser
 * already keeps saved sign-ins.
 *
 * This is the whole design. A separate "sign in with a passkey" button asks somebody to
 * remember a decision they made months ago, and most people do not — they look for the
 * field, because that is what a sign-in screen is. So the passkey goes where they are
 * already looking, under their own name, and tapping it is the entire act.
 *
 * Started on load rather than on a click: the browser needs the offer in hand before
 * the field is touched, or it has nothing to show. The request then waits, quietly,
 * until somebody picks it or leaves the page.
 */
export function usePasskeyAutofill(door: PasskeyDoor, clientId: string): void {
  useEffect(() => {
    let live = true;
    const controller = new AbortController();

    void (async () => {
      if (!(await browserSupportsWebAuthnAutofill()) || !live) return;
      try {
        const { options } = await postJson<PasskeyOptions>(`/api/${door}/auth/passkey/options`, {});
        if (!live) return;
        const credential = await startAuthentication({
          optionsJSON: options as never,
          useBrowserAutofill: true,
        });
        if (!live) return;
        await postJson(`/api/${door}/auth/passkey`, { credential, clientId });
        window.location.reload();
      } catch {
        // Leaving the page, or a browser holding no passkey, both end up here. Neither
        // is a fault and neither is worth saying anything about: the email field below
        // is still exactly where it was.
      }
    })();

    return () => {
      live = false;
      controller.abort();
    };
  }, [door, clientId]);
}

/** This browser's own name for itself, so a session belongs to a device. */
export function deviceClientId(): string {
  const key = "wvcea_client_id";
  const existing = window.localStorage.getItem(key);
  if (existing) return existing;
  const created = crypto.randomUUID();
  window.localStorage.setItem(key, created);
  return created;
}

export interface PasskeyState {
  supported: boolean;
  passkeys: readonly PasskeySummary[];
  busy: boolean;
  error: string | null;
  add: (label?: string) => Promise<void>;
  remove: (id: string) => Promise<void>;
  reload: () => Promise<void>;
}

/** The devices on this account, for somebody who is already signed in. */
export function usePasskeys(signedIn: boolean, door: PasskeyDoor = "attendee"): PasskeyState {
  const [passkeys, setPasskeys] = useState<readonly PasskeySummary[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const supported = passkeysAvailable();

  const reload = useCallback(async () => {
    if (!signedIn || !supported) return;
    try {
      const response = await fetch(`/api/${door}/passkeys`, { credentials: "same-origin" });
      if (!response.ok) return;
      const body = (await response.json()) as PasskeyList;
      setPasskeys(body.passkeys);
    } catch {
      // A list that will not load is not worth an error on the screen; the buttons
      // still work and the list fills in next time.
    }
  }, [door, signedIn, supported]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const add = useCallback(async (label?: string) => {
    setBusy(true);
    setError(null);
    try {
      const { options } = await postJson<PasskeyOptions>(`/api/${door}/passkeys/options`, {});
      const credential = await startRegistration({ optionsJSON: options as never });
      await postJson<PasskeySummary>(`/api/${door}/passkeys/enrol`, { credential, label: label ?? null });
      await reload();
    } catch (reason) {
      // Somebody closing the system prompt is not a fault and must not read like one.
      setError(cancelled(reason) ? null : describe(reason));
    } finally {
      setBusy(false);
    }
  }, [door, reload]);

  const remove = useCallback(async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      const response = await fetch(`/api/${door}/passkeys/${encodeURIComponent(id)}`, {
        method: "DELETE",
        credentials: "same-origin",
      });
      if (!response.ok) throw new Error(await messageFrom(response));
      await reload();
    } catch (reason) {
      setError(describe(reason));
    } finally {
      setBusy(false);
    }
  }, [door, reload]);

  return { supported, passkeys, busy, error, add, remove, reload };
}

export function cancelled(reason: unknown): boolean {
  return reason instanceof Error && (reason.name === "NotAllowedError" || reason.name === "AbortError");
}

export function describe(reason: unknown): string {
  return reason instanceof Error && reason.message ? reason.message : "That did not work. Try again in a moment.";
}
